/**
 * usePartyDialogEffects
 *
 * Side-effect hook for the party dialog.
 * Handles data loading (countries, states, business identifiers), form
 * initialisation when the dialog opens, entity data fetch for edit mode,
 * duplicate-check triggering, and auto-save to localStorage.
 */

import { useCallback, useEffect } from 'react';

import type { Party, PartyType } from '@/gen/proto';

import { EMPTY_FORM_DATA } from './party-dialog-helpers';
import {
  fetchEntityBusinessIdentifiers,
  initializeForm,
} from './usePartyDialogEffects-init';
import {
  type BusinessIdentifierType,
  type Country,
  type State,
  getBusinessIdentifiers,
  getCountries,
  getCountryByCode,
  getStates,
} from '../../../services/geolocation-service';

import type { PartyFormData } from './party-dialog-helpers';
import type { Entity } from '../../../services/entity-service';




// Re-export for tests/callers that import internals (optional).
export { initializeForm, initializeEditMode, initializeCreateMode } from './usePartyDialogEffects-init';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UsePartyDialogEffectsParams {
  isOpen: boolean;
  editingParty?: Party | null;
  partyType: PartyType;
  existingPartyCodes: string[];
  baseCurrency: string;
  orgId: string;
  ledgerId: string;
  formData: PartyFormData;
  setFormData: React.Dispatch<React.SetStateAction<PartyFormData>>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setBillingStates: (states: State[]) => void;
  setBillingCountry: (country: Country | null) => void;
  setLoadingCountries: (v: boolean) => void;
  setCountriesLoaded: (v: boolean) => void;
  setBusinessIdentifiers: (ids: BusinessIdentifierType[]) => void;
  setDuplicateEntities: (entities: Entity[]) => void;
  setDuplicateEntitiesParties: (map: Map<string, Party[]>) => void;
  setSelectedEntity: (entity: Entity | null) => void;
  setShowDuplicateWarning: (v: boolean) => void;
  loadFromLocalStorage: () => PartyFormData | null;
  saveToLocalStorage: (data: PartyFormData) => void;
  checkDuplicates: (params: { entityName?: string; countryCode?: string; identifiers: { gstin?: string; pan?: string; cin?: string; tan?: string } }) => void;
  isCountriesLoaded: boolean;
  selectedEntity: Entity | null;
}

// ---------------------------------------------------------------------------
// Inner hooks
// ---------------------------------------------------------------------------

function usePreloadCountries(isOpen: boolean, setLoadingCountries: (v: boolean) => void, setCountriesLoaded: (v: boolean) => void): void {
  useEffect(() => {
    if (!isOpen) { setCountriesLoaded(false); return; }
    setLoadingCountries(true);
    setCountriesLoaded(false);
    void getCountries()
      .then(() => { setLoadingCountries(false); setCountriesLoaded(true); })
      .catch(() => { setLoadingCountries(false); setCountriesLoaded(true); });
  }, [isOpen, setCountriesLoaded, setLoadingCountries]);
}

interface BillingDataSetters {
  setBillingStates: (states: State[]) => void;
  setBillingCountry: (country: Country | null) => void;
  setBusinessIdentifiers: (ids: BusinessIdentifierType[]) => void;
}

function useBillingDataForCountry(countryCode: string | undefined, isCountriesLoaded: boolean, setters: BillingDataSetters): void {
  const loadBillingStates = useCallback(async (cc: string): Promise<void> => {
    try { setters.setBillingStates(await getStates(cc)); }
    catch { setters.setBillingStates([]); }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- setters object literal is re-created every render; underlying setState functions are stable
  }, []);

  useEffect(() => {
    if (!isCountriesLoaded) { return; }
    const cc = countryCode ?? 'IN';
    void loadBillingStates(cc);
    void getCountryByCode(cc).then(c => setters.setBillingCountry(c ?? null));
    void getBusinessIdentifiers(cc)
      .then(ids => setters.setBusinessIdentifiers(ids.filter(id => id.is_active && id.country_code !== '**')))
      .catch(() => setters.setBusinessIdentifiers([]));
    // setters and loadBillingStates intentionally excluded: setters is a fresh
    // object literal every render (created in usePartyDialogEffects), so
    // including it causes the effect to fire every render — re-fetching states,
    // country, and business identifiers in an infinite loop. Underlying setState
    // functions are stable, so reading the latest setters via closure is safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode, isCountriesLoaded]);
}

function useResetOnClose(
  isOpen: boolean,
  baseCurrency: string,
  setFormData: React.Dispatch<React.SetStateAction<PartyFormData>>,
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>,
): void {
  useEffect(() => {
    if (!isOpen) {
      setFormData(prev => ({
        ...EMPTY_FORM_DATA,
        partyType: prev.partyType,
        roles: prev.roles,
        defaultCurrency: baseCurrency,
      }));
      setErrors({});
    }
  }, [isOpen, baseCurrency, setFormData, setErrors]);
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function usePartyDialogEffects(params: UsePartyDialogEffectsParams): void {
  const {
    isOpen, editingParty, partyType, existingPartyCodes, baseCurrency, orgId, ledgerId, formData,
    setFormData, setErrors, setBillingStates, setBillingCountry,
    setLoadingCountries, setCountriesLoaded, setBusinessIdentifiers,
    setDuplicateEntities, setDuplicateEntitiesParties, setSelectedEntity,
    setShowDuplicateWarning, loadFromLocalStorage, saveToLocalStorage,
    checkDuplicates, isCountriesLoaded, selectedEntity,
  } = params;

  usePreloadCountries(isOpen, setLoadingCountries, setCountriesLoaded);
  useBillingDataForCountry(formData.billingAddress.countryCode, isCountriesLoaded, {
    setBillingStates, setBillingCountry, setBusinessIdentifiers,
  });
  useResetOnClose(isOpen, baseCurrency, setFormData, setErrors);

  useEffect(() => {
    if (!isOpen || !isCountriesLoaded) { return; }
    void initializeForm({
      editingParty, partyType, existingPartyCodes, baseCurrency, orgId, ledgerId,
      loadFromLocalStorage, setFormData, setDuplicateEntities,
      setDuplicateEntitiesParties, setSelectedEntity, setShowDuplicateWarning, setErrors,
    });
  }, [isOpen, editingParty, partyType, existingPartyCodes, isCountriesLoaded, baseCurrency, orgId, ledgerId, loadFromLocalStorage,
    setFormData, setDuplicateEntities, setDuplicateEntitiesParties, setSelectedEntity, setShowDuplicateWarning, setErrors]);

  useEffect(() => {
    if (!isOpen || !editingParty?.entityId) { return; }
    void fetchEntityBusinessIdentifiers(editingParty.entityId, setFormData);
  }, [isOpen, editingParty?.entityId, setFormData]);

  useEffect(() => {
    if (!editingParty && !selectedEntity) {
      void checkDuplicates({
        entityName: formData.entityName ?? undefined,
        countryCode: formData.billingAddress.countryCode ?? 'IN',
        identifiers: {
          gstin: formData.gstin ?? undefined,
          pan: formData.pan ?? undefined,
          cin: formData.cin ?? undefined,
          tan: formData.tan ?? undefined,
        },
      });
    }
  }, [
    formData.entityName, formData.billingAddress.countryCode,
    formData.gstin, formData.pan, formData.cin, formData.tan,
    editingParty, selectedEntity, checkDuplicates,
  ]);

  useEffect(() => {
    if (isOpen && !editingParty) { saveToLocalStorage(formData); }
  }, [formData, isOpen, editingParty, saveToLocalStorage]);
}
